import { StatusColor } from '../constants/statusColors.constant';
import i18n from 'i18next';

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [r * 255, g * 255, b * 255];
}

function hue2rgb(p: number, q: number, t: number) {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function strToRgb(code: string) {
  const r16 = code.slice(1, 3);
  const g16 = code.slice(3, 5);
  const b16 = code.slice(5, 7);
  return [r16, g16, b16].map(s => Number.parseInt(s, 16)) as [
    number,
    number,
    number,
  ];
}

function rgbToStr(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map(num => Math.round(num).toString(16))
      .map(str => (str.length > 1 ? str : `0${str}`))
      .join('')
  );
}

const COLOR_LIST = [
  '#2B66FF',
  '#5195FF',
  '#79C3FF',
  '#A2F1FC',
  '#76E0F4',
  '#07CDD4',
  '#3ADDB4',
  '#6FEA99',
  '#A5FB80',
  '#DFE755',
  '#FEAF2C',
  '#FF8D24',
  '#DC8130',
  '#F9B87B',
  '#FFD4AD',
  '#F4AD9F',
  '#E3858D',
  '#D55B79',
  '#C3488C',
  '#9E23B2',
  '#703CC6',
  '#8072DD',
  '#B7B5FB',
  '#F3BFFB',
  '#B380DF',
  '#9253D0',
];

export const getColorByIndex = (index: number, active: boolean = false) => {
  const colorIndex = index % COLOR_LIST.length;
  const darkenRank = Math.floor(index / COLOR_LIST.length);
  const color = COLOR_LIST[colorIndex];
  if (!darkenRank && !active) return color;
  const darkenHsl = rgbToHsl(...strToRgb(color));
  darkenHsl[1] = Math.max(0.4, darkenHsl[1] - 0.2 * darkenRank);
  darkenHsl[2] = Math.max(0.4, darkenHsl[2] - 0.2 * darkenRank);
  if (active) {
    darkenHsl[2] = Math.min(1, darkenHsl[2] + 0.1);
  }
  return rgbToStr(...hslToRgb(...darkenHsl));
};

export const getStatusColor = (color: string) => {
  return i18n.exists(`statusColor.${color}`)
    ? `STATUSES_${color.toUpperCase()}`
    : color ||
    `STATUSES_${StatusColor.GREY.toUpperCase()}`
}